import { LoadingSpinner } from "@/components/UIComponents";
import { ServiceFactory } from "@/services";
import { AuthData, Employee } from "@/types/employee";
import type { FC, ReactNode } from "react";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";

const employeesService = ServiceFactory.getEmployeesService();
interface AuthProviderProps {
  children: ReactNode;
}

interface AuthContextValue {
  token: string | null;
  isLoading: boolean;
  data: AuthData | null;
  isAdmin: boolean;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const getAuthKey = (): string | null => {
  const envValue = import.meta.env?.VITE_AUTH_TOKEN;
  if (typeof envValue === "string" && envValue.length > 0) {
    return envValue;
  }

  return null;
};

const fetchEmployee = async (): Promise<AuthData | Employee> => {
  const employeeIdFromEnv = import.meta.env?.VITE_SPECIFIC_EMPLOYEE;
  if (employeeIdFromEnv) {
    const employee = await employeesService.getEmployeeById(employeeIdFromEnv);
    return employee;
  }
  const employee = await employeesService.getCurrentEmployee();
  return employee;
};

const saveJwtInMemory = (jwt: string): void => {
  localStorage.setItem("authKey", jwt);
};

const initialState: AuthContextValue = {
  token: "",
  isLoading: true,
  data: null,
  isAdmin: false,
};

export const AuthProvider: FC<AuthProviderProps> = ({ children }) => {
  const { search } = useLocation();
  const urlSearchParams = new URLSearchParams(search);
  const authKeyFromUrl = urlSearchParams.get("authorization");

  const [state, setState] = useState<AuthContextValue>(initialState);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (state.token === authKeyFromUrl) return;
      setLoading(true);

      const authKey = authKeyFromUrl ?? getAuthKey();

      if (authKey) saveJwtInMemory(authKey);

      try {
        const employee = await fetchEmployee();
        setState({
          token: authKey,
          isLoading: false,
          data: employee as AuthData,
          isAdmin: (employee as AuthData)?.roleId === -3,
        });
      } catch (error) {
        console.error("Error loading auth context", error);
        setState({ ...initialState, isLoading: false });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [authKeyFromUrl]);

  const value = useMemo<AuthContextValue>(() => state, [state]);

  return (
    <AuthContext.Provider value={value}>
      {loading ? <LoadingSpinner /> : children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export type { AuthContextValue };
